import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    ArrowLeft, CaretRight, CheckCircle, Clock, Sparkle, Ruler, Funnel,
    ShareNetwork, Warning, LinkSimple,
} from "@phosphor-icons/react";
import { DoctorSidebarCard, MoreCard } from "@/pages/PublicArticlePage";
import { CallbackModal } from "@/pages/DoctorLandingPage";

/**
 * PublicCasePage — /dr/:doctorSlug/case/:caseSlug
 *
 * Individual URL for a case study. Same architecture as the article page:
 *   - Breadcrumb bar
 *   - Two-column: case body (left) + sticky doctor sidebar (right)
 *   - Before / After image row when both are present
 *   - Chip strip showing complexity · concern · treatment · duration · trays
 *   - More from this doctor strip at bottom
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCENTS = {
    teal:  { base: "hsl(192 90% 33%)",  soft: "hsl(192 90% 96%)",  ring: "hsl(192 90% 88%)",  text: "hsl(192 90% 25%)" },
    rose:  { base: "hsl(340 75% 45%)",  soft: "hsl(340 75% 97%)",  ring: "hsl(340 75% 90%)",  text: "hsl(340 75% 35%)" },
    amber: { base: "hsl(28 88% 45%)",   soft: "hsl(28 88% 97%)",   ring: "hsl(28 88% 88%)",   text: "hsl(28 88% 35%)" },
    slate: { base: "hsl(215 60% 32%)",  soft: "hsl(215 60% 97%)",  ring: "hsl(215 60% 88%)",  text: "hsl(215 60% 25%)" },
};
const pickAccent = (n) => ACCENTS[n] || ACCENTS.teal;

const CONCERN_LABELS = {
    crowding:  "Crowding",
    spacing:   "Spacing / Gaps",
    overbite:  "Overbite / Deep bite",
    underbite: "Underbite",
    complex:   "Complex bite",
    other:     "Other",
};
const COMPLEXITY_LABELS = { mild: "Mild", moderate: "Moderate", complex: "Complex" };


export default function PublicCasePage() {
    const { doctorSlug, caseSlug } = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true, cs: null, doctor: null, more: [] });
    const [callbackOpen, setCallbackOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setState((s) => ({ ...s, loading: true }));
            try {
                const { data } = await axios.get(`${API}/public/doctor-page/${doctorSlug}/case/${caseSlug}`);
                if (cancelled) return;
                setState({
                    loading: false,
                    cs: data.case_study,
                    doctor: data.doctor,
                    more: data.more || [],
                });
                if (data.canonical_slug && data.canonical_slug !== doctorSlug) {
                    navigate(`/dr/${data.canonical_slug}/case/${caseSlug}`, { replace: true });
                }
            } catch {
                if (cancelled) return;
                setState({ loading: false, cs: null, doctor: null, more: [] });
            }
        })();
        return () => { cancelled = true; };
    }, [doctorSlug, caseSlug, navigate]);

    if (state.loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-sm text-muted-foreground">Loading case study…</div>
            </div>
        );
    }
    if (!state.cs) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
                <div className="text-center max-w-md">
                    <CheckCircle size={48} className="mx-auto text-slate-300 mb-3" />
                    <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Case study not found</h1>
                    <p className="text-sm text-slate-500 mb-4">This case may have been removed or the URL has changed.</p>
                    <Link to={`/dr/${doctorSlug}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                        <ArrowLeft size={14} /> Back to Dr. profile
                    </Link>
                </div>
            </div>
        );
    }

    const cs = state.cs.data || {};
    const doc = state.doctor;
    const accent = pickAccent(doc?.accent);
    const shareUrl = typeof window !== "undefined" ? window.location.href : "";

    const share = async () => {
        const shareData = { title: cs.title, text: cs.description, url: shareUrl };
        try {
            if (navigator.share) await navigator.share(shareData);
            else { await navigator.clipboard.writeText(shareUrl); alert("URL copied to clipboard"); }
        } catch { /* cancelled */ }
    };

    return (
        <div className="min-h-screen bg-slate-50" data-testid="public-case-page">
            <title>{`${cs.title} · Case Study · ${doc.name} · Invisique™`}</title>
            <meta name="description" content={(cs.description || cs.treatment)?.slice(0, 160)} />
            <meta property="og:title" content={cs.title} />
            <meta property="og:description" content={(cs.description || cs.treatment)?.slice(0, 160)} />
            <meta property="og:type" content="article" />
            <meta property="og:url" content={shareUrl} />
            {cs.before_image && <meta property="og:image" content={cs.before_image} />}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "MedicalWebPage",
                name: cs.title,
                description: cs.description,
                mainContentOfPage: cs.description,
                about: cs.concern_type ? CONCERN_LABELS[cs.concern_type] : "Orthodontics",
                datePublished: cs.published_at || state.cs.created_at,
                dateModified: state.cs.updated_at || cs.published_at || state.cs.created_at,
                image: [cs.before_image, cs.after_image].filter(Boolean),
                author: {
                    "@type": "Person",
                    name: doc.name,
                    jobTitle: doc.specialization,
                    // iter100.169 — Link the author byline back to the doctor's
                    // marketing profile so Google can associate the case study
                    // with the provider's E-E-A-T entity.
                    url: doc.slug && typeof window !== "undefined"
                        ? `${window.location.origin}/dr/${doc.slug}`
                        : undefined,
                    worksFor: doc.clinic_name ? {
                        "@type": "Organization",
                        name: doc.clinic_name,
                        address: doc.clinic_city || undefined,
                    } : undefined,
                },
                mainEntityOfPage: { "@type": "WebPage", "@id": shareUrl },
            })}} />

            {/* Breadcrumb */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-2 text-[12px] text-slate-600 flex-wrap">
                    <Link to="/" className="hover:text-slate-900">Home</Link>
                    <CaretRight size={10} weight="bold" className="opacity-50" />
                    <Link to={`/dr/${doc.slug}`} className="hover:text-slate-900">{doc.name}</Link>
                    <CaretRight size={10} weight="bold" className="opacity-50" />
                    <span className="text-slate-400">Case studies</span>
                    <CaretRight size={10} weight="bold" className="opacity-50" />
                    <span className="text-slate-900 font-medium truncate max-w-[240px] sm:max-w-[440px]">{cs.title}</span>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 lg:py-10">
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-8 lg:gap-10">
                    {/* Left column: case body */}
                    <article className="min-w-0" data-testid="case-body">
                        {/* iter100.45 — Prominent "Back to profile" pill so
                            readers landing directly from Google always have
                            an obvious way back to the doctor's main page. */}
                        <Link
                            to={`/dr/${doc.slug}`}
                            className="inline-flex items-center gap-1.5 mb-4 h-8 px-3 rounded-full border text-[12px] font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-colors"
                            style={{ borderColor: accent.ring }}
                            data-testid="case-back-to-profile"
                        >
                            <ArrowLeft size={12} weight="bold" />
                            Back to {doc.name}&apos;s profile
                        </Link>
                        <div className="flex items-start justify-between gap-4 mb-2">
                            <div className="text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5" style={{ color: accent.text }}>
                                <CheckCircle size={12} weight="fill" />
                                Case study by {doc.name}
                            </div>
                            <button
                                type="button"
                                onClick={share}
                                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-600 hover:text-slate-900 transition-colors"
                                data-testid="case-share-btn"
                            >
                                <ShareNetwork size={14} weight="regular" />
                                Share
                            </button>
                        </div>
                        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 leading-tight tracking-tight mb-4" data-testid="case-title">
                            {cs.title}
                        </h1>

                        {/* Chip strip */}
                        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="case-chips">
                            {cs.concern_type && (
                                <Chip icon={<Funnel size={11} weight="fill" />} label={CONCERN_LABELS[cs.concern_type] || cs.concern_type} accent={accent} />
                            )}
                            {cs.complexity && (
                                <Chip icon={<Warning size={11} weight="fill" />} label={`${COMPLEXITY_LABELS[cs.complexity] || cs.complexity} complexity`} accent={accent} />
                            )}
                            {cs.treatment && (
                                <Chip icon={<Sparkle size={11} weight="fill" />} label={cs.treatment} accent={accent} />
                            )}
                            {cs.duration && (
                                <Chip icon={<Clock size={11} weight="fill" />} label={cs.duration} accent={accent} />
                            )}
                            {cs.aligner_count > 0 && (
                                <Chip icon={<Ruler size={11} weight="fill" />} label={`${cs.aligner_count} aligners`} accent={accent} />
                            )}
                        </div>

                        {/* Before / After images */}
                        {(cs.before_image || cs.after_image) && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-testid="case-images">
                                {cs.before_image && (
                                    <figure className="rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
                                        <img src={cs.before_image} alt="Before" className="w-full h-56 object-cover" />
                                        <figcaption className="px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-slate-600">Before</figcaption>
                                    </figure>
                                )}
                                {cs.after_image && (
                                    <figure className="rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
                                        <img src={cs.after_image} alt="After" className="w-full h-56 object-cover" />
                                        <figcaption className="px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-slate-600" style={{ color: accent.text }}>After</figcaption>
                                    </figure>
                                )}
                            </div>
                        )}

                        {/* Body / description */}
                        <div className="space-y-5 text-[15.5px] leading-[1.75] text-slate-800" data-testid="case-description">
                            {(cs.description || "").split(/\n\n+/).map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>

                        {/* iter100.46 — Removed the body-bottom "Book a consultation"
                            card. The sticky sidebar already carries the primary
                            CTAs (Book / Call / Request callback); stacking a
                            duplicate CTA under the description read as
                            overselling, especially on mobile where the sidebar
                            reflows directly beneath the body. Kept a light,
                            single-line closer instead so the page still ends
                            with a clear next step without shouting. */}
                        <div className="mt-10 pt-6 border-t border-slate-200 flex items-center justify-between gap-4 flex-wrap" data-testid="case-closer-row">
                            <p className="text-[13px] text-slate-500">
                                Have a case like this? {doc.name} can help you plan yours.
                            </p>
                            <button
                                type="button"
                                onClick={() => setCallbackOpen(true)}
                                className="inline-flex items-center gap-1.5 text-[13px] font-semibold hover:gap-2 transition-all"
                                style={{ color: accent.base }}
                                data-testid="case-closer-cta"
                            >
                                Request a callback <CaretRight size={11} weight="bold" />
                            </button>
                        </div>
                    </article>

                    {/* Right column: sticky doctor card */}
                    <aside className="lg:sticky lg:top-6 h-fit" data-testid="case-doctor-sidebar">
                        <DoctorSidebarCard
                            doctor={doc}
                            accent={accent}
                            onOpenCallback={() => setCallbackOpen(true)}
                        />
                    </aside>
                </div>

                {/* iter100.172 — Author-picked "Related from Dr. X" strip.
                    Same pattern as PublicArticlePage — curated internal
                    links shown ABOVE the auto "More from" strip. */}
                {Array.isArray(cs.related_items) && cs.related_items.length > 0 && (
                    <section
                        className="mt-12 border-t border-slate-200 pt-8"
                        data-testid="case-related-strip"
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <span
                                className="h-6 w-6 rounded-full grid place-items-center text-white"
                                style={{ backgroundColor: accent.base }}
                            >
                                <LinkSimple size={12} weight="bold" />
                            </span>
                            <h2 className="font-display text-lg font-bold text-slate-900">
                                Related from {doc.name}
                            </h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {cs.related_items.map((item) => (
                                <MoreCard
                                    key={item.id}
                                    item={{ id: item.id, kind: item.kind, data: item }}
                                    doctorSlug={doc.slug}
                                    accent={accent}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {/* More strip */}
                {state.more.length > 0 && (
                    <section className="mt-12 border-t border-slate-200 pt-8" data-testid="case-more-strip">
                        <h2 className="font-display text-lg font-bold text-slate-900 mb-4">
                            More from {doc.name}
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {state.more.map((item) => (
                                <MoreCard key={item.id} item={item} doctorSlug={doc.slug} accent={accent} />
                            ))}
                        </div>
                    </section>
                )}
            </div>

            {callbackOpen && (
                <CallbackModal
                    doc={doc}
                    slug={doc.slug}
                    accent={accent}
                    onClose={() => setCallbackOpen(false)}
                    defaultNotes={`I read the "${cs.title}" case study on ${doc.name}'s page and would like a similar consultation.`}
                />
            )}
        </div>
    );
}


function Chip({ icon, label, accent }) {
    return (
        <span
            className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-semibold border"
            style={{ backgroundColor: accent.soft, borderColor: accent.ring, color: accent.text }}
        >
            {icon}{label}
        </span>
    );
}
